// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * Subscription tier (uppercase to match comfy-api)
 */
export const zSubscriptionTier = z.enum([
    'FREE',
    'STANDARD',
    'CREATOR',
    'PRO',
    'FOUNDERS_EDITION',
    'TEAM'
]);

/**
 * Workspace entity annotated with the requesting user's role.
 */
export const zWorkspaceWithRole = z.object({
    created_at: z.string().datetime(),
    id: z.string(),
    joined_at: z.string().datetime(),
    name: z.string(),
    role: z.enum(['owner', 'member']),
    subscription_tier: zSubscriptionTier.optional(),
    type: z.enum(['personal', 'team'])
});

/**
 * Abbreviated workspace metadata used in list responses.
 */
export const zWorkspaceSummary = z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['personal', 'team'])
});

/**
 * Metadata for a workspace-scoped API key (secret is never returned).
 */
export const zWorkspaceApiKeyInfo = z.object({
    created_at: z.string().datetime(),
    description: z.string().max(5000),
    expires_at: z.string().datetime().optional(),
    id: z.string().uuid(),
    key_prefix: z.string(),
    last_used_at: z.string().datetime().optional(),
    name: z.string(),
    revoked_at: z.string().datetime().optional(),
    user_id: z.string(),
    workspace_id: z.string()
});

/**
 * Full workspace entity with configuration and ownership details.
 */
export const zWorkspace = z.object({
    created_at: z.string().datetime(),
    id: z.string(),
    name: z.string(),
    type: z.enum(['personal', 'team'])
});

/**
 * Metadata for a single workflow version.
 */
export const zWorkflowVersionResponse = z.object({
    created_at: z.string().datetime(),
    created_by: z.string(),
    id: z.string(),
    latest_version: z.number().int(),
    version: z.number().int()
});

/**
 * Full workflow version including the serialized workflow JSON.
 */
export const zWorkflowVersionContentResponse = z.object({
    created_at: z.string().datetime(),
    created_by: z.string(),
    dependency_asset_ids: z.array(z.string()).optional(),
    id: z.string(),
    version: z.number().int(),
    workflow_json: z.record(z.unknown())
});

/**
 * Workflow metadata captured at mint time. The subsequent PUT body is the
 * raw workflow JSON document and nothing else, so everything that would
 * normally accompany workflow_json in POST /api/workflows is declared
 * here instead.
 *
 */
export const zWorkflowUploadUrlRequest = z.object({
    default_view: z.enum(['workflow', 'app']).optional(),
    description: z.string().max(2000).optional(),
    forked_from_workflow_id: z.string().max(128).optional(),
    forked_from_workflow_version_id: z.string().max(128).optional(),
    name: z.string().max(255).optional()
});

/**
 * Reference to the parent workflow from which this workflow was forked.
 */
export const zWorkflowForkedFrom = z.object({
    workflow_id: z.string().optional(),
    workflow_version_id: z.string().optional()
});

/**
 * Full workflow entity including metadata and version history.
 */
export const zWorkflowResponse = z.object({
    created_at: z.string().datetime(),
    created_by: z.string(),
    default_view: z.enum(['workflow', 'app']).optional(),
    description: z.string().optional(),
    forked_from: zWorkflowForkedFrom.optional(),
    id: z.string(),
    latest_version: z.number().int(),
    name: z.string().optional(),
    updated_at: z.string().datetime()
});

/**
 * Lightweight asset reference used in workflow publishing payloads.
 */
export const zAssetInfo = z.object({
    id: z.string(),
    in_library: z.boolean(),
    model: z.boolean(),
    name: z.string(),
    preview_url: z.string(),
    public: z.boolean(),
    storage_url: z.string()
});

/**
 * Publishing metadata for a workflow shared to the Hub.
 */
export const zWorkflowPublishInfo = z.object({
    assets: z.array(zAssetInfo),
    listed: z.boolean(),
    publish_time: z.string().datetime().nullish(),
    share_id: z.string(),
    workflow_id: z.string()
});

/**
 * Pagination metadata included in list responses. Supports both legacy
 * offset/limit pagination and cursor-based pagination. When cursor-based
 * pagination is used, `next_cursor` is the primary pagination token and
 * `offset`/`total` may be zero.
 *
 */
export const zPaginationInfo = z.object({
    has_more: z.boolean(),
    limit: z.number().int().gte(1),
    next_cursor: z.string().optional(),
    offset: z.number().int().gte(0),
    total: z.number().int().gte(0)
});

/**
 * Paginated list of saved workflows.
 */
export const zWorkflowListResponse = z.object({
    data: z.array(zWorkflowResponse),
    pagination: zPaginationInfo
});

/**
 * Response containing assets associated with a workflow.
 */
export const zWorkflowApiAssetsResponse = z.object({
    assets: z.array(zAssetInfo)
});

/**
 * Request body for querying assets associated with a workflow.
 */
export const zWorkflowApiAssetsRequest = z.object({
    workflow_api_json: z.record(z.unknown())
});

/**
 * Details of a single validation error encountered during asset operations.
 */
export const zValidationError = z.object({
    code: z.string(),
    field: z.string(),
    message: z.string()
});

/**
 * Result of validating a set of asset operations.
 */
export const zValidationResult = z.object({
    errors: z.array(zValidationError).optional(),
    is_valid: z.boolean(),
    warnings: z.array(zValidationError).optional()
});

/**
 * User information response
 */
export const zUserResponse = z.object({
    id: z.string(),
    status: z.string()
});

/**
 * User data listing entry with file metadata (path, size, modification time).
 */
export const zUserDataResponseFull = z.object({
    modified: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    path: z.string().optional(),
    size: z.number().int().optional()
});

export const zUsageBalance = z.object({
    amount_micros: z.number().optional(),
    cloud_credit_balance_micros: z.number().optional(),
    currency: z.string().optional(),
    prepaid_balance_micros: z.number().optional()
});

export const zUsageSummary = z.object({
    balance: zUsageBalance.optional(),
    spend_micros: z.number()
});

export const zUsageBucket = z.object({
    cost_micros: z.number(),
    group_key: z.string(),
    period_end: z.string().datetime(),
    period_start: z.string().datetime()
});

export const zUsageBreakdownRow = z.object({
    cost_micros: z.number(),
    group_key: z.string(),
    share: z.number()
});

export const zUsageTimeSeries = z.object({
    breakdown: z.array(zUsageBreakdownRow),
    buckets: z.array(zUsageBucket),
    ending_before: z.string().datetime(),
    granularity: z.enum([
        'hour',
        'day',
        'month'
    ]),
    group_by: z.enum([
        'model',
        'endpoint',
        'product'
    ]),
    groups: z.array(z.string()),
    starting_on: z.string().datetime(),
    summary: zUsageSummary
});

/**
 * A minted single-use upload grant.
 */
export const zUploadGrantResponse = z.object({
    expires_in: z.number().int(),
    upload_path: z.string()
});

/**
 * Request body for updating an existing workspace's settings.
 */
export const zUpdateWorkspaceRequest = z.object({
    name: z.string().min(1).max(100).optional()
});

/**
 * Request body for updating an existing saved workflow.
 */
export const zUpdateWorkflowRequest = z.object({
    default_view: z.enum(['workflow', 'app']).optional(),
    description: z.string().optional(),
    name: z.string().optional()
});

/**
 * Request body for updating an existing user secret.
 */
export const zUpdateSecretRequest = z.object({
    name: z.string().min(1).max(255).optional(),
    secret_value: z.string().min(1).optional()
});

/**
 * Request body for changing a workspace member's role.
 */
export const zUpdateMemberRoleRequest = z.object({
    role: z.enum(['owner', 'member'])
});

/**
 * Request body for updating an existing Hub profile.
 */
export const zUpdateHubProfileRequest = z.object({
    avatar_token: z.string().nullish(),
    description: z.string().optional(),
    display_name: z.string().optional(),
    website_urls: z.array(z.string()).optional()
});

/**
 * Pre/post-discount price for a team credit stop, in cents.
 */
export const zTeamCreditStopPrice = z.object({
    list_price_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    price_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' })
});

/**
 * A selectable preset on the team pricing slider. Echoed on subscribe via
 * team_credit_stop_id; the backend owns the resolved amounts. credits is a
 * RAW monthly credit count (not cents). Save% is derived by the FE as
 * (list_price_cents - price_cents) / list_price_cents.
 *
 */
export const zTeamCreditStop = z.object({
    credits: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    id: z.string(),
    monthly: zTeamCreditStopPrice,
    yearly: zTeamCreditStopPrice
});

/**
 * Credit-stop ladder for the pricing slider (BE-1254). Returned by GET /api/billing/plans for every workspace regardless of the caller's token or workspace type (the personal/team distinction was removed); omitted only when the catalog defines no stops.
 */
export const zTeamCreditStops = z.object({
    default_stop_index: z.number().int(),
    stops: z.array(zTeamCreditStop)
});

/**
 * The team credit stop a workspace is currently subscribed to: the
 * per-workspace slider choice recorded at subscribe time
 * (workspace_subscriptions.team_credit_stop_id). Amounts are owned by the
 * catalog, not the subscription row. Returned on GET /api/billing/status
 * for per-credit Team plans (BE-1254).
 *
 */
export const zTeamCreditStopSummary = z.object({
    credits_monthly: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    id: z.string(),
    stop_usd: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' })
});

/**
 * Task data for list views
 */
export const zTaskEntry = z.object({
    completed_at: z.string().datetime().optional(),
    create_time: z.string().datetime(),
    id: z.string().uuid(),
    started_at: z.string().datetime().optional(),
    status: z.enum([
        'created',
        'running',
        'completed',
        'failed'
    ]),
    task_name: z.string()
});

/**
 * Paginated list of background tasks for the authenticated user.
 */
export const zTasksListResponse = z.object({
    pagination: zPaginationInfo,
    tasks: z.array(zTaskEntry)
});

/**
 * Full task details including payload and result
 */
export const zTaskResponse = z.object({
    completed_at: z.string().datetime().optional(),
    create_time: z.string().datetime(),
    error_message: z.string().optional(),
    id: z.string().uuid(),
    idempotency_key: z.string(),
    payload: z.record(z.unknown()),
    result: z.record(z.unknown()).optional(),
    started_at: z.string().datetime().optional(),
    status: z.enum([
        'created',
        'running',
        'completed',
        'failed'
    ]),
    task_name: z.string(),
    update_time: z.string().datetime()
});

/**
 * Response after adding, updating, or removing tags on an asset.
 */
export const zTagsModificationResponse = z.object({
    added: z.array(z.string()).optional(),
    already_present: z.array(z.string()).optional(),
    not_present: z.array(z.string()).optional(),
    removed: z.array(z.string()).optional(),
    total_tags: z.array(z.string())
});

/**
 * Metadata for a single tag that can be applied to assets.
 */
export const zTagInfo = z.object({
    count: z.number().int(),
    name: z.string()
});

/**
 * System statistics response
 */
export const zSystemStatsResponse = z.object({
    devices: z.array(z.object({
        name: z.string(),
        type: z.string(),
        vram_free: z.number().optional(),
        vram_total: z.number().optional()
    })),
    system: z.object({
        argv: z.array(z.string()),
        cloud_version: z.string().optional(),
        comfyui_frontend_version: z.string().optional(),
        comfyui_version: z.string(),
        deploy_environment: z.string().optional(),
        embedded_python: z.boolean(),
        os: z.string(),
        python_version: z.string(),
        pytorch_version: z.string(),
        ram_free: z.number(),
        ram_total: z.number(),
        workflow_templates_version: z.string().optional()
    })
});

/**
 * Billing period (uppercase to match comfy-api)
 */
export const zSubscriptionDuration = z.enum(['MONTHLY', 'ANNUAL']);

/**
 * Response after successfully subscribing to a billing plan.
 */
export const zSubscribeResponse = z.object({
    billing_op_id: z.string(),
    effective_at: z.string().datetime().optional(),
    payment_method_url: z.string().optional(),
    status: z.enum([
        'subscribed',
        'needs_payment_method',
        'pending_payment'
    ])
});

/**
 * Request body for subscribing a workspace to a billing plan.
 */
export const zSubscribeRequest = z.object({
    billing_cycle: z.enum(['monthly', 'yearly']).optional(),
    cancel_url: z.string().optional(),
    idempotency_key: z.string().optional(),
    plan_slug: z.string(),
    return_url: z.string().optional(),
    team_credit_stop_id: z.string().optional()
});

/**
 * User secret metadata (the secret value itself is never returned after creation).
 */
export const zSecretResponse = z.object({
    created_at: z.string().datetime(),
    credential_type: z.enum(['api_key', 'gcp_service_account']).optional(),
    id: z.string().uuid(),
    last_used_at: z.string().datetime().optional(),
    name: z.string(),
    provider: z.string().optional(),
    updated_at: z.string().datetime()
});

/**
 * One way a provider's credential can be entered. When a provider exposes more than one option, clients present a sub-selection; the selected option's credential_type is sent on CreateSecretRequest.
 */
export const zCredentialOption = z.object({
    credential_type: z.enum(['api_key', 'gcp_service_account']),
    input_type: z.enum(['text', 'json_file']),
    label: z.string()
});

/**
 * A provider the user may configure a secret for, with its display label and credential-entry options.
 */
export const zSecretProvider = z.object({
    credential_options: z.array(zCredentialOption).optional(),
    id: z.string(),
    label: z.string().optional()
});

/**
 * The providers available to the authenticated user in the current workspace.
 */
export const zSecretProvidersResponse = z.object({
    data: z.array(zSecretProvider)
});

/**
 * List of user secrets with metadata only.
 */
export const zSecretListResponse = z.object({
    data: z.array(zSecretResponse)
});

/**
 * Response after accepting a resubscribe request.
 */
export const zResubscribeResponse = z.object({
    billing_op_id: z.string(),
    message: z.string().optional(),
    status: z.enum(['active', 'pending'])
});

/**
 * Request body for reactivating a previously cancelled subscription.
 */
export const zResubscribeRequest = z.object({
    idempotency_key: z.string().optional()
});

/**
 * Response after a queue management action (delete or clear).
 */
export const zQueueManageResponse = z.object({
    cleared: z.boolean().optional(),
    deleted: z.array(z.string()).optional()
});

/**
 * Request to manage queue operations
 */
export const zQueueManageRequest = z.object({
    clear: z.boolean().optional(),
    delete: z.array(z.string()).optional()
});

/**
 * Queue information with pending and running jobs
 */
export const zQueueInfo = z.object({
    queue_pending: z.array(z.tuple([
        z.unknown(),
        z.unknown(),
        z.unknown(),
        z.unknown(),
        z.unknown()
    ])).optional(),
    queue_running: z.array(z.tuple([
        z.unknown(),
        z.unknown(),
        z.unknown(),
        z.unknown(),
        z.unknown()
    ])).optional()
});

/**
 * Full detail of a publicly published workflow on the Hub.
 */
export const zPublishedWorkflowDetail = z.object({
    assets: z.array(zAssetInfo),
    listed: z.boolean(),
    name: z.string(),
    publish_time: z.string().datetime().nullish(),
    share_id: z.string(),
    workflow_id: z.string(),
    workflow_json: z.record(z.unknown())
});

/**
 * Request body for publishing workflow assets to the Hub.
 */
export const zPublishWorkflowAssetsRequest = z.object({
    asset_ids: z.array(z.string())
});

/**
 * Request body for publishing or updating a workflow on the Hub.
 */
export const zPublishHubWorkflowRequest = z.object({
    asset_ids: z.array(z.string()),
    custom_nodes: z.array(z.string()).optional(),
    description: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    models: z.array(z.string()).optional(),
    name: z.string(),
    sample_image_tokens_or_urls: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    thumbnail_comparison_token_or_url: z.string().optional(),
    thumbnail_token_or_url: z.string().optional(),
    thumbnail_type: z.enum([
        'image',
        'video',
        'image_comparison'
    ]).optional(),
    tutorial_url: z.string().optional(),
    username: z.string(),
    workflow_filename: z.string()
});

/**
 * Response returned after successfully queuing a workflow prompt.
 */
export const zPromptResponse = z.object({
    node_errors: z.record(z.unknown()).optional(),
    number: z.number().optional(),
    prompt_id: z.string().uuid().optional()
});

/**
 * Request body for submitting a ComfyUI workflow prompt for execution.
 */
export const zPromptRequest = z.object({
    extra_data: z.record(z.unknown()).optional(),
    front: z.boolean().optional(),
    number: z.number().optional(),
    partial_execution_targets: z.array(z.string()).optional(),
    prompt: z.record(z.unknown()),
    workflow_id: z.string().optional(),
    workflow_version_id: z.string().optional()
});

/**
 * Metadata about the currently running and queued prompts.
 */
export const zPromptInfo = z.object({
    exec_info: z.object({
        queue_remaining: z.number().int().optional()
    }).optional()
});

/**
 * Error response for ComfyUI prompt execution.
 */
export const zPromptErrorResponse = z.record(z.unknown());

/**
 * Summary of seat costs based on current workspace members
 */
export const zPlanSeatSummary = z.object({
    seat_count: z.number().int(),
    total_cost_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    total_credits_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' })
});

/**
 * Plan information for preview display
 */
export const zPreviewPlanInfo = z.object({
    credits_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    duration: zSubscriptionDuration,
    period_end: z.string().datetime().optional(),
    period_start: z.string().datetime().optional(),
    price_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    seat_summary: zPlanSeatSummary,
    slug: z.string(),
    tier: zSubscriptionTier
});

/**
 * Itemized cost preview for a pending subscription change.
 */
export const zPreviewSubscribeResponse = z.object({
    allowed: z.boolean(),
    cost_next_period_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    cost_today_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    credits_next_period_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    credits_today_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    current_plan: zPreviewPlanInfo.optional(),
    effective_at: z.string().datetime(),
    is_immediate: z.boolean(),
    new_plan: zPreviewPlanInfo,
    reason: z.string().optional(),
    transition_type: z.enum([
        'new_subscription',
        'upgrade',
        'downgrade',
        'duration_change'
    ])
});

/**
 * Request body for previewing the cost of a plan subscription change.
 */
export const zPreviewSubscribeRequest = z.object({
    plan_slug: z.string(),
    team_credit_stop_id: z.string().optional()
});

/**
 * Reason why a plan is unavailable
 */
export const zPlanAvailabilityReason = z.enum([
    'same_plan',
    'incompatible_transition',
    'requires_team',
    'requires_personal',
    'exceeds_max_seats'
]);

/**
 * Availability and eligibility information for a billing plan.
 */
export const zPlanAvailability = z.object({
    available: z.boolean(),
    reason: zPlanAvailabilityReason.optional()
});

/**
 * Billing plan details including pricing, limits, and features.
 */
export const zPlan = z.object({
    availability: zPlanAvailability,
    credits_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    duration: zSubscriptionDuration,
    max_seats: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    price_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    seat_summary: zPlanSeatSummary,
    slug: z.string(),
    tier: zSubscriptionTier
});

/**
 * An outstanding workspace invitation that has not yet been accepted.
 */
export const zPendingInvite = z.object({
    email: z.string().email(),
    expires_at: z.string().datetime(),
    id: z.string(),
    invited_at: z.string().datetime(),
    token: z.string().optional()
});

/**
 * Response containing a redirect URL to the payment portal.
 */
export const zPaymentPortalResponse = z.object({
    url: z.string()
});

/**
 * Request body for generating a payment portal session URL.
 */
export const zPaymentPortalRequest = z.object({
    return_url: z.string().optional()
});

/**
 * RFC 6749 §5.1 successful token response.
 */
export const zOAuthTokenResponse = z.object({
    access_token: z.string(),
    expires_in: z.number().int(),
    refresh_token: z.string(),
    scope: z.string(),
    token_type: z.enum(['Bearer'])
});

/**
 * RFC 6749 §5.2 error response.
 */
export const zOAuthTokenError = z.object({
    error: z.string(),
    error_description: z.string().optional()
});

/**
 * RFC 7591 §3.2.1 successful registration response.
 */
export const zOAuthRegisterResponse = z.object({
    application_type: z.enum(['native', 'web']),
    client_id: z.string(),
    client_id_issued_at: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    client_name: z.string().optional(),
    grant_types: z.array(z.string()),
    redirect_uris: z.array(z.string()),
    response_types: z.array(z.string()),
    token_endpoint_auth_method: z.enum(['none'])
});

/**
 * RFC 7591 §2 client metadata document. Only the fields the server honors are listed; presence of `scope` or `resource_grants` in the request is rejected (`invalid_client_metadata`) because those are server-owned for dynamic clients. `additionalProperties: false` mirrors the runtime middleware that rejects any unknown metadata key.
 *
 */
export const zOAuthRegisterRequest = z.object({
    application_type: z.enum(['native', 'web']).optional(),
    client_name: z.string().max(100).optional(),
    client_uri: z.string().nullish(),
    contacts: z.array(z.string()).nullish(),
    grant_types: z.array(z.enum(['authorization_code', 'refresh_token'])).optional(),
    jwks: z.record(z.unknown()).nullish(),
    jwks_uri: z.string().nullish(),
    logo_uri: z.string().nullish(),
    policy_uri: z.string().nullish(),
    redirect_uris: z.array(z.string()).min(1).max(5),
    resource_grants: z.record(z.array(z.string())).nullish(),
    response_types: z.array(z.enum(['code'])).optional(),
    scope: z.string().nullish(),
    software_id: z.string().nullish(),
    software_version: z.string().nullish(),
    token_endpoint_auth_method: z.enum(['none']).optional(),
    tos_uri: z.string().nullish()
});

/**
 * RFC 7591 §3.2.2 error response.
 */
export const zOAuthRegisterError = z.object({
    error: z.enum(['invalid_redirect_uri', 'invalid_client_metadata']),
    error_description: z.string().nullish()
});

/**
 * Standard error response with a machine-readable code and human-readable message.
 */
export const zErrorResponse = z.object({
    code: z.string(),
    details: z.record(z.unknown()).optional(),
    message: z.string()
});

/**
 * Union of the two 400 shapes /oauth/register can emit. `OAuthRegisterError` is the handler-shaped RFC 7591 §3.2.2 error; `ErrorResponse` is the strict-server binding-layer error fired when the request body fails OpenAPI-schema validation before the handler runs, normalized to the standard {code, message} shape by the custom Echo HTTPErrorHandler (BE-1178).
 *
 */
export const zOAuthRegisterBadRequestResponse = z.union([
    zOAuthRegisterError,
    zErrorResponse
]);

/**
 * OAuth 2.1 protected-resource metadata (RFC 9728).
 */
export const zOAuthProtectedResourceMetadata = z.object({
    authorization_servers: z.array(z.string().url()),
    bearer_methods_supported: z.array(z.string()).optional(),
    resource: z.string().url(),
    scopes_supported: z.array(z.string())
});

/**
 * One workspace option presented in the OAuth consent challenge. Promoted to a named schema so the generated Go type is referenceable in handlers and tests rather than re-declared as an anonymous struct at every callsite.
 *
 */
export const zOAuthConsentChallengeWorkspace = z.object({
    id: z.string(),
    name: z.string(),
    role: z.enum(['owner', 'member']),
    type: z.enum(['personal', 'team'])
});

/**
 * Server-side state describing the OAuth consent decision the user is being asked to make. Returned by GET /oauth/authorize when a valid Cloud session exists; the frontend renders the consent UI from this payload and POSTs the decision back. Browser never sees the original OAuth params on resume.
 *
 */
export const zOAuthConsentChallenge = z.object({
    client_display_name: z.string(),
    csrf_token: z.string(),
    oauth_request_id: z.string().uuid(),
    redirect_uri: z.string().url(),
    resource_display_name: z.string(),
    scopes: z.array(z.string()),
    workspaces: z.array(zOAuthConsentChallengeWorkspace)
});

/**
 * Redirect target produced after a JSON consent submission. The frontend must navigate the browser to this URL so custom-scheme client callbacks work without relying on fetch-visible 302 headers.
 */
export const zOAuthAuthorizeRedirectResponse = z.object({
    redirect_url: z.string().url()
});

/**
 * OAuth 2.1 authorization-server metadata (RFC 8414).
 */
export const zOAuthAuthorizationServerMetadata = z.object({
    authorization_endpoint: z.string().url(),
    code_challenge_methods_supported: z.array(z.string()),
    grant_types_supported: z.array(z.string()),
    issuer: z.string().url(),
    jwks_uri: z.string().url(),
    registration_endpoint: z.string().url().optional(),
    response_types_supported: z.array(z.string()),
    scopes_supported: z.array(z.string()).optional(),
    token_endpoint: z.string().url(),
    token_endpoint_auth_methods_supported: z.array(z.string())
});

/**
 * Metadata describing a single ComfyUI node type and its inputs/outputs.
 */
export const zNodeInfo = z.object({
    api_node: z.boolean().optional(),
    category: z.string().optional(),
    deprecated: z.boolean().optional(),
    description: z.string().optional(),
    display_name: z.string().optional(),
    experimental: z.boolean().optional(),
    input: z.record(z.unknown()).optional(),
    input_order: z.record(z.array(z.string())).optional(),
    name: z.string().optional(),
    output: z.array(z.string()).optional(),
    output_is_list: z.array(z.boolean()).optional(),
    output_name: z.array(z.string()).optional(),
    output_node: z.boolean().optional(),
    output_tooltips: z.array(z.string()).optional(),
    python_module: z.string().optional()
});

/**
 * Represents a folder containing models
 */
export const zModelFolder = z.object({
    folders: z.array(z.string()),
    name: z.string()
});

/**
 * Represents a model file with metadata
 */
export const zModelFile = z.object({
    name: z.string(),
    pathIndex: z.number().int()
});

/**
 * Workspace member with profile and role information.
 */
export const zMember = z.object({
    email: z.string().email(),
    id: z.string(),
    is_original_owner: z.boolean(),
    joined_at: z.string().datetime(),
    name: z.string(),
    role: z.enum(['owner', 'member'])
});

/**
 * Paginated list of workspaces the authenticated user belongs to.
 */
export const zListWorkspacesResponse = z.object({
    workspaces: z.array(zWorkspaceWithRole)
});

/**
 * List of API keys associated with the current workspace.
 */
export const zListWorkspaceApiKeysResponse = z.object({
    api_keys: z.array(zWorkspaceApiKeyInfo)
});

/**
 * Paginated list of available asset tags.
 */
export const zListTagsResponse = z.object({
    has_more: z.boolean(),
    tags: z.array(zTagInfo),
    total: z.number().int()
});

/**
 * List of members in the current workspace.
 */
export const zListMembersResponse = z.object({
    members: z.array(zMember),
    pagination: zPaginationInfo
});

/**
 * List of pending invitations for the current workspace.
 */
export const zListInvitesResponse = z.object({
    invites: z.array(zPendingInvite)
});

/**
 * Represents a user-owned asset (image, video, or other generated output).
 */
export const zAsset = z.object({
    created_at: z.string().datetime(),
    display_name: z.string().nullish(),
    file_path: z.string().nullish(),
    hash: z.string().regex(/^blake3:[a-f0-9]{64}$/).optional(),
    id: z.string().uuid(),
    is_immutable: z.boolean().optional(),
    job_id: z.string().uuid().nullish(),
    last_access_time: z.string().datetime().optional(),
    metadata: z.record(z.unknown()).readonly().optional(),
    mime_type: z.string().optional(),
    name: z.string(),
    preview_id: z.string().uuid().nullish(),
    preview_url: z.string().url().optional(),
    short_url: z.string().nullish(),
    size: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    tags: z.array(z.string()).optional(),
    updated_at: z.string().datetime(),
    user_metadata: z.record(z.unknown()).optional()
});

/**
 * Paginated list of assets belonging to the authenticated user.
 */
export const zListAssetsResponse = z.object({
    assets: z.array(zAsset),
    has_more: z.boolean(),
    next_cursor: z.string().optional(),
    total: z.number().int()
});

/**
 * Reference to a Hub label by ID.
 */
export const zLabelRef = z.object({
    display_name: z.string(),
    name: z.string()
});

/**
 * A single JSON Web Key entry within a JWKS response.
 */
export const zJwkKey = z.object({
    alg: z.string(),
    crv: z.string(),
    kid: z.string(),
    kty: z.string(),
    use: z.string(),
    x: z.string(),
    y: z.string()
});

/**
 * JSON Web Key Set containing the public keys used to verify Cloud JWTs.
 */
export const zJwksResponse = z.object({
    keys: z.array(zJwkKey)
});

/**
 * Detailed execution error information from ComfyUI
 */
export const zExecutionError = z.object({
    current_inputs: z.record(z.unknown()),
    current_outputs: z.record(z.unknown()),
    exception_message: z.string(),
    exception_type: z.string(),
    node_id: z.string(),
    node_type: z.string(),
    traceback: z.array(z.string())
});

/**
 * Lightweight job data for list views (workflow and full outputs excluded)
 */
export const zJobEntry = z.object({
    create_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    execution_end_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    execution_error: zExecutionError.optional(),
    execution_start_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    id: z.string().uuid(),
    outputs_count: z.number().int().optional(),
    preview_output: z.record(z.unknown()).optional(),
    status: z.enum([
        'pending',
        'in_progress',
        'completed',
        'failed',
        'cancelled'
    ]),
    workflow_id: z.string().optional()
});

/**
 * Paginated list of jobs for the authenticated user.
 */
export const zJobsListResponse = z.object({
    jobs: z.array(zJobEntry),
    pagination: zPaginationInfo
});

/**
 * Response for POST /api/jobs/cancel.
 */
export const zJobsCancelResponse = z.object({
    cancelled: z.array(z.string())
});

/**
 * Request to cancel multiple jobs by ID.
 */
export const zJobsCancelRequest = z.object({
    job_ids: z.array(z.string().uuid()).min(1).max(100)
});

/**
 * Job status information
 */
export const zJobStatusResponse = z.object({
    assigned_inference: z.string().nullish(),
    created_at: z.string().datetime(),
    error_message: z.string().nullish(),
    id: z.string().uuid(),
    last_state_update: z.string().datetime().optional(),
    status: z.enum([
        'waiting_to_dispatch',
        'pending',
        'in_progress',
        'completed',
        'error',
        'cancelled'
    ]),
    updated_at: z.string().datetime()
});

/**
 * Full job details including workflow and outputs
 */
export const zJobDetailResponse = z.object({
    create_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    execution_end_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    execution_error: zExecutionError.optional(),
    execution_meta: z.record(z.unknown()).optional(),
    execution_start_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    execution_status: z.record(z.unknown()).optional(),
    id: z.string().uuid(),
    outputs: z.record(z.unknown()).optional(),
    outputs_count: z.number().int().optional(),
    preview_output: z.record(z.unknown()).optional(),
    status: z.enum([
        'pending',
        'in_progress',
        'completed',
        'failed',
        'cancelled'
    ]),
    update_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    user_id: z.string().optional(),
    workflow: z.record(z.unknown()).optional(),
    workflow_id: z.string().optional(),
    workspace_id: z.string().optional()
});

/**
 * Response for POST /api/jobs/{job_id}/cancel. Returned on both fresh cancels and idempotent no-ops.
 */
export const zJobCancelResponse = z.object({
    cancelled: z.boolean()
});

/**
 * Request body for minting an input-image upload grant.
 */
export const zInputUploadUrlRequest = z.object({
    content_type: z.string().max(64)
});

/**
 * Result of redeeming an input-image upload grant. Identical in shape to
 * the POST /api/upload/image response.
 *
 */
export const zInputUploadResponse = z.object({
    name: z.string(),
    subfolder: z.string(),
    type: z.string()
});

/**
 * Response after importing published workflow assets.
 */
export const zImportPublishedAssetsResponse = z.object({
    assets: z.array(zAssetInfo)
});

/**
 * Request body for importing assets from a published workflow.
 */
export const zImportPublishedAssetsRequest = z.object({
    published_asset_ids: z.array(z.string()),
    share_id: z.string().nullish()
});

/**
 * Public workflow status. NULL in the database is represented as pending in API responses.
 */
export const zHubWorkflowStatus = z.enum([
    'pending',
    'approved',
    'rejected',
    'deprecated'
]);

/**
 * Abbreviated Hub profile used in workflow listings.
 */
export const zHubProfileSummary = z.object({
    avatar_url: z.string().optional(),
    display_name: z.string().optional(),
    username: z.string()
});

/**
 * Entry in the curated workflow template gallery shown on the home page.
 */
export const zHubWorkflowTemplateEntry = z.object({
    contentTemplate: z.string().optional(),
    date: z.string().optional(),
    description: z.string().optional(),
    extendedDescription: z.string().optional(),
    faqItems: z.array(z.object({
        answer: z.string(),
        question: z.string()
    })).optional(),
    howToUse: z.array(z.string()).optional(),
    includeOnDistributions: z.array(z.string()).optional(),
    io: z.object({
        inputs: z.array(z.record(z.unknown())).optional(),
        outputs: z.array(z.record(z.unknown())).optional()
    }).optional(),
    isEssential: z.boolean().optional(),
    logos: z.array(z.record(z.unknown())).optional(),
    mediaSubtype: z.string().optional(),
    mediaType: z.string().optional(),
    metaDescription: z.string().optional(),
    models: z.array(z.string()).optional(),
    name: z.string(),
    openSource: z.boolean().optional(),
    profile: zHubProfileSummary.optional(),
    requiresCustomNodes: z.array(z.string()).optional(),
    searchRank: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    shareId: z.string().optional(),
    size: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    status: zHubWorkflowStatus,
    suggestedUseCases: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    thumbnailComparisonUrl: z.string().optional(),
    thumbnailUrl: z.string().optional(),
    thumbnailVariant: z.string().optional(),
    title: z.string(),
    tutorialUrl: z.string().optional(),
    usage: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    vram: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional()
});

/**
 * Abbreviated Hub workflow metadata used in search and listing results.
 */
export const zHubWorkflowSummary = z.object({
    custom_nodes: z.array(zLabelRef).optional(),
    description: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    models: z.array(zLabelRef).optional(),
    name: z.string(),
    profile: zHubProfileSummary,
    publish_time: z.string().datetime().nullish(),
    sample_image_urls: z.array(z.string()).optional(),
    share_id: z.string(),
    status: zHubWorkflowStatus,
    tags: z.array(zLabelRef).optional(),
    thumbnail_comparison_url: z.string().optional(),
    thumbnail_type: z.enum([
        'image',
        'video',
        'image_comparison'
    ]).optional(),
    thumbnail_url: z.string().optional(),
    tutorial_url: z.string().optional()
});

/**
 * Full Hub workflow detail including versions, assets, and statistics.
 */
export const zHubWorkflowDetail = z.object({
    assets: z.array(zAssetInfo),
    custom_nodes: z.array(zLabelRef).optional(),
    description: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    models: z.array(zLabelRef).optional(),
    name: z.string(),
    profile: zHubProfileSummary,
    publish_time: z.string().datetime().nullish(),
    sample_image_urls: z.array(z.string()).optional(),
    share_id: z.string(),
    status: zHubWorkflowStatus,
    tags: z.array(zLabelRef).optional(),
    thumbnail_comparison_url: z.string().optional(),
    thumbnail_type: z.enum([
        'image',
        'video',
        'image_comparison'
    ]).optional(),
    thumbnail_url: z.string().optional(),
    tutorial_url: z.string().optional(),
    workflow_id: z.string(),
    workflow_json: z.record(z.unknown())
});

/**
 * Paginated list of Hub workflows matching search criteria.
 */
export const zHubWorkflowListResponse = z.object({
    next_cursor: z.string().optional(),
    workflows: z.array(z.union([zHubWorkflowSummary, zHubWorkflowDetail]))
});

/**
 * Response indicating whether a Hub username is available.
 */
export const zHubUsernameCheckResponse = z.object({
    available: z.boolean(),
    suggestions: z.array(z.string()).optional(),
    username: z.string(),
    validation_error: z.string().optional()
});

/**
 * Full public profile for a Hub creator.
 */
export const zHubProfile = z.object({
    avatar_url: z.string().optional(),
    description: z.string().optional(),
    display_name: z.string().optional(),
    username: z.string(),
    website_urls: z.array(z.string()).optional()
});

/**
 * Metadata for a single Hub label.
 */
export const zHubLabelInfo = z.object({
    description: z.string().optional(),
    display_name: z.string(),
    name: z.string(),
    type: z.enum([
        'tag',
        'model',
        'custom_node'
    ])
});

/**
 * List of available Hub labels for categorizing workflows.
 */
export const zHubLabelListResponse = z.object({
    labels: z.array(zHubLabelInfo)
});

/**
 * Response containing a signed upload URL and the target asset path.
 */
export const zHubAssetUploadUrlResponse = z.object({
    public_url: z.string(),
    token: z.string(),
    upload_url: z.string()
});

/**
 * Request body for requesting a signed upload URL for a Hub asset.
 */
export const zHubAssetUploadUrlRequest = z.object({
    content_type: z.string(),
    filename: z.string()
});

/**
 * History entry with prompt_id and execution data
 */
export const zHistoryEntry = z.object({
    create_time: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    meta: z.record(z.unknown()).optional(),
    outputs: z.record(z.unknown()).optional(),
    prompt: z.object({
        extra_data: z.record(z.unknown()).optional(),
        priority: z.number().optional(),
        prompt_id: z.string().optional()
    }).optional(),
    prompt_id: z.string(),
    status: z.record(z.unknown()).optional(),
    workflow_id: z.string().optional()
});

/**
 * Execution history response with history array.
 * Returns an object with a "history" key containing an array of history entries.
 * Each entry includes prompt_id as a property along with execution data.
 *
 */
export const zHistoryResponse = z.object({
    history: z.array(zHistoryEntry)
});

/**
 * Request to manage history operations
 */
export const zHistoryManageRequest = z.object({
    clear: z.boolean().optional(),
    delete: z.array(z.string()).optional()
});

/**
 * History entry with full prompt data
 */
export const zHistoryDetailEntry = z.object({
    meta: z.record(z.unknown()).optional(),
    outputs: z.record(z.unknown()).optional(),
    prompt: z.object({
        extra_data: z.record(z.unknown()).optional(),
        outputs_to_execute: z.array(z.string()).optional(),
        priority: z.number().optional(),
        prompt: z.record(z.unknown()).optional(),
        prompt_id: z.string().optional()
    }).optional(),
    status: z.record(z.unknown()).optional()
});

/**
 * Detailed execution history response for a specific prompt.
 * Returns a dictionary with prompt_id as key and full history data as value.
 *
 */
export const zHistoryDetailResponse = z.record(zHistoryDetailEntry);

/**
 * Metadata for a global subgraph blueprint (without full data)
 */
export const zGlobalSubgraphInfo = z.object({
    data: z.string().optional(),
    info: z.object({
        node_pack: z.string()
    }),
    name: z.string(),
    source: z.string()
});

/**
 * Full data for a global subgraph blueprint
 */
export const zGlobalSubgraphData = z.object({
    data: z.string(),
    info: z.object({
        node_pack: z.string()
    }),
    name: z.string(),
    source: z.string()
});

/**
 * Individual file entry within a full user data response.
 */
export const zGetUserDataResponseFullFile = z.object({
    modified: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    path: z.string().optional(),
    size: z.number().int().optional()
});

/**
 * List of user data file entries (each with path, size, and modification time) returned when full_info=true.
 */
export const zGetUserDataResponseFull = z.array(zGetUserDataResponseFullFile);

/**
 * Request body for forking an existing workflow into the user's account.
 */
export const zForkWorkflowRequest = z.object({
    name: z.string().optional(),
    source_version: z.number().int()
});

/**
 * Response after submitting feedback
 */
export const zFeedbackResponse = z.record(z.unknown());

/**
 * Request to submit user feedback
 */
export const zFeedbackRequest = z.object({
    content: z.string().optional(),
    metadata: z.record(z.unknown()).optional(),
    rating: z.number().int().gte(1).lte(5).optional(),
    type: z.enum([
        'missing_nodes',
        'general',
        'missing_models'
    ])
});

/**
 * Response containing a signed download URL for an exported asset archive.
 */
export const zExportDownloadUrlResponse = z.object({
    expires_at: z.string().datetime().optional(),
    url: z.string()
});

/**
 * Response containing the issued Cloud JWT and its expiry.
 */
export const zExchangeTokenResponse = z.object({
    expires_at: z.string().datetime(),
    permissions: z.array(z.string()),
    role: z.enum(['owner', 'member']),
    token: z.string(),
    workspace: zWorkspaceSummary
});

/**
 * Optional request body for the token exchange endpoint. The Firebase JWT
 * being exchanged is supplied via the `Authorization: Bearer` header; this
 * body only carries workspace-selection input.
 *
 */
export const zExchangeTokenRequest = z.object({
    workspace_id: z.string().optional()
});

/**
 * Result of redeeming a desktop login code.
 */
export const zDesktopLoginCodeRedeemResponse = z.object({
    status: z.enum(['redeemed'])
});

/**
 * Request to claim a desktop login code for the authenticated user.
 */
export const zDesktopLoginCodeRedeemRequest = z.object({
    code: z.string()
});

/**
 * Exchange poll result. Pending until the code is redeemed in the browser.
 */
export const zDesktopLoginCodeExchangeResponse = z.object({
    custom_token: z.string().optional(),
    status: z.enum(['pending', 'complete'])
});

/**
 * Request to exchange a redeemed login code for a custom token.
 */
export const zDesktopLoginCodeExchangeRequest = z.object({
    code: z.string(),
    code_verifier: z.string().min(43).max(128)
});

/**
 * A freshly minted desktop login code and its polling parameters.
 */
export const zDesktopLoginCodeCreateResponse = z.object({
    code: z.string(),
    expires_in: z.number().int(),
    poll_interval: z.number().int()
});

/**
 * Request to mint a desktop login code.
 */
export const zDesktopLoginCodeCreateRequest = z.object({
    app_version: z.string().min(1).max(64),
    code_challenge: z.string().min(43).max(128),
    installation_id: z.string().min(8).max(128).regex(/^[A-Za-z0-9._-]+$/).optional(),
    platform: z.string().min(1).max(32)
});

/**
 * Response after deleting a session cookie
 */
export const zDeleteSessionResponse = z.object({
    success: z.boolean()
});

/**
 * Request body for creating a new workspace.
 */
export const zCreateWorkspaceRequest = z.object({
    name: z.string().min(1).max(100)
});

/**
 * Response containing the newly created workspace API key.
 */
export const zCreateWorkspaceApiKeyResponse = z.object({
    created_at: z.string().datetime(),
    description: z.string().max(5000),
    expires_at: z.string().datetime().optional(),
    id: z.string().uuid(),
    key: z.string(),
    key_prefix: z.string(),
    name: z.string()
});

/**
 * Request body for creating a new workspace-scoped API key.
 */
export const zCreateWorkspaceApiKeyRequest = z.object({
    description: z.string().max(5000).optional(),
    expires_at: z.string().datetime().optional(),
    name: z.string()
});

/**
 * Request body for creating a new version of a saved workflow.
 */
export const zCreateWorkflowVersionRequest = z.object({
    base_version: z.number().int(),
    workflow_json: z.record(z.unknown())
});

/**
 * Request body for creating a new saved workflow.
 */
export const zCreateWorkflowRequest = z.object({
    default_view: z.enum(['workflow', 'app']).optional(),
    description: z.string().optional(),
    forked_from_workflow_id: z.string().optional(),
    forked_from_workflow_version_id: z.string().optional(),
    name: z.string().optional(),
    workflow_json: z.record(z.unknown())
});

/**
 * Response after successfully purchasing a credit top-up.
 */
export const zCreateTopupResponse = z.object({
    amount_cents: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    billing_op_id: z.string(),
    status: z.enum([
        'pending',
        'completed',
        'failed'
    ]),
    topup_id: z.string()
});

/**
 * Request body for purchasing a one-time credit top-up.
 */
export const zCreateTopupRequest = z.object({
    amount_cents: z.coerce.bigint().gte(BigInt(500)).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    idempotency_key: z.string().optional()
});

/**
 * Response after creating a session cookie
 */
export const zCreateSessionResponse = z.object({
    expiresIn: z.number().int().optional(),
    success: z.boolean()
});

/**
 * Request body for creating a new user secret.
 */
export const zCreateSecretRequest = z.object({
    credential_type: z.enum(['api_key', 'gcp_service_account']).optional(),
    name: z.string().min(1).max(255),
    provider: z.string().max(64).optional(),
    secret_value: z.string().min(1)
});

/**
 * Request body for inviting a user to a workspace.
 */
export const zCreateInviteRequest = z.object({
    email: z.string().email()
});

/**
 * Request body for creating a new Hub profile.
 */
export const zCreateHubProfileRequest = z.object({
    avatar_token: z.string().optional(),
    description: z.string().optional(),
    display_name: z.string().optional(),
    username: z.string(),
    website_urls: z.array(z.string()).optional(),
    workspace_id: z.string()
});

/**
 * Response after successfully cancelling a subscription.
 */
export const zCancelSubscriptionResponse = z.object({
    billing_op_id: z.string(),
    cancel_at: z.string().datetime()
});

/**
 * Request body for cancelling the current subscription.
 */
export const zCancelSubscriptionRequest = z.object({
    idempotency_key: z.string().optional()
});

/**
 * Response after bulk-revoking API keys for a workspace member.
 */
export const zBulkRevokeApiKeysResponse = z.object({
    revoked_count: z.number().int().gte(0)
});

/**
 * Payment lifecycle status
 */
export const zBillingStatus = z.enum([
    'awaiting_payment_method',
    'pending_payment',
    'paid',
    'payment_failed',
    'paused',
    'inactive'
]);

/**
 * Current billing and subscription status for a workspace.
 */
export const zBillingStatusResponse = z.object({
    billing_rail: z.enum([
        'legacy_stripe',
        'metronome',
        'stripe'
    ]).optional(),
    billing_status: zBillingStatus.optional(),
    cancel_at: z.string().datetime().optional(),
    has_funds: z.boolean(),
    is_active: z.boolean(),
    plan_slug: z.string().optional(),
    renewal_date: z.string().datetime().optional(),
    subscription_duration: zSubscriptionDuration.optional(),
    subscription_status: z.enum([
        'active',
        'ended',
        'canceled'
    ]).optional(),
    subscription_tier: zSubscriptionTier.optional(),
    team_credit_stop: zTeamCreditStopSummary.nullable()
});

/**
 * List of available billing plans for subscription.
 */
export const zBillingPlansResponse = z.object({
    current_plan_slug: z.string().optional(),
    plans: z.array(zPlan),
    team_credit_stops: zTeamCreditStops.optional()
});

/**
 * Status of an asynchronous billing operation.
 */
export const zBillingOpStatusResponse = z.object({
    completed_at: z.string().datetime().optional(),
    error_message: z.string().optional(),
    id: z.string(),
    started_at: z.string().datetime(),
    status: z.enum([
        'pending',
        'succeeded',
        'failed'
    ])
});

/**
 * A single history event. The cloud history-events store is the single source of truth for both billing events (charges, credits, adjustments) and user-facing usage events.
 */
export const zBillingEvent = z.object({
    createdAt: z.string().datetime(),
    event_id: z.string(),
    event_type: z.string(),
    params: z.record(z.unknown()).optional()
});

/**
 * Paginated list of billing events for a workspace.
 */
export const zBillingEventsResponse = z.object({
    events: z.array(zBillingEvent),
    limit: z.number().int(),
    page: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int()
});

/**
 * Current credit balance and usage details for a workspace.
 */
export const zBillingBalanceResponse = z.object({
    amount_micros: z.number(),
    cloud_credit_balance_micros: z.number().optional(),
    currency: z.string(),
    effective_balance_micros: z.number().optional(),
    pending_charges_micros: z.number().optional(),
    prepaid_balance_micros: z.number().optional()
});

/**
 * Response returned when an existing asset is successfully updated.
 */
export const zAssetUpdated = z.object({
    display_name: z.string().nullish(),
    file_path: z.string().nullish(),
    hash: z.string().regex(/^blake3:[a-f0-9]{64}$/).optional(),
    id: z.string().uuid(),
    job_id: z.string().uuid().nullish(),
    mime_type: z.string().optional(),
    name: z.string().optional(),
    tags: z.array(z.string()).optional(),
    updated_at: z.string().datetime(),
    user_metadata: z.record(z.unknown()).optional()
});

/**
 * Histogram of tag counts used for refining asset search results.
 */
export const zAssetTagHistogramResponse = z.object({
    tag_counts: z.record(z.number().int())
});

/**
 * Metadata for a remotely hosted asset resolved by URL.
 */
export const zAssetMetadataResponse = z.object({
    content_length: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }),
    content_type: z.string().optional(),
    filename: z.string().optional(),
    name: z.string().optional(),
    preview_image: z.string().optional(),
    tags: z.array(z.string()).optional(),
    validation: zValidationResult.optional()
});

/**
 * Acknowledgement of an async asset download task; clients poll GET /api/tasks/{task_id} for status.
 */
export const zAssetDownloadResponse = z.object({
    message: z.string().optional(),
    status: z.enum([
        'created',
        'running',
        'completed',
        'failed'
    ]),
    task_id: z.string().uuid()
});

/**
 * Response returned when a new asset is successfully created.
 */
export const zAssetCreated = zAsset.and(z.object({
    created_new: z.boolean()
}));

/**
 * Acknowledgement that a turn was accepted. The agent runs asynchronously; output streams over the WebSocket (agent_message_delta, agent_tool_call, draft_patch, agent_message_done).
 */
export const zAgentTurnAccepted = z.object({
    message_id: z.string(),
    thread_id: z.string()
});

/**
 * One agent thread row for the history sidebar.
 */
export const zAgentThreadSummary = z.object({
    created_at: z.string(),
    id: z.string(),
    last_message_at: z.string(),
    message_count: z.number().int().gte(0),
    preview: z.string(),
    status: z.enum(['active', 'archived']),
    title: z.string(),
    updated_at: z.string(),
    workflow_id: z.string()
});

/**
 * A paginated page of the caller's agent threads.
 */
export const zAgentThreadListResponse = z.object({
    pagination: zPaginationInfo,
    threads: z.array(zAgentThreadSummary)
});

/**
 * Result of creating an agent thread.
 */
export const zAgentThreadCreated = z.object({
    thread_id: z.string()
});

/**
 * Optional body for creating an agent thread.
 */
export const zAgentThreadCreateRequest = z.object({
    workflow_id: z.string().optional()
});

/**
 * A user turn posted to the agent.
 */
export const zAgentPostMessageRequest = z.object({
    attachments: z.array(z.string()).optional(),
    content: z.string(),
    current_tab: z.string().optional(),
    draft: z.object({
        content: z.record(z.unknown()).optional(),
        version: z.number().int().nullish()
    }).optional(),
    open_tabs: z.array(z.object({
        name: z.string().optional(),
        workflow_id: z.string()
    })).optional(),
    selection: z.record(z.unknown()).optional(),
    workflow_id: z.string().optional()
});

/**
 * A persisted message in an agent thread.
 */
export const zAgentMessage = z.object({
    content: z.record(z.unknown()).optional(),
    id: z.string(),
    role: z.enum([
        'user',
        'assistant',
        'tool',
        'system'
    ]),
    seq: z.number().int(),
    status: z.enum([
        'streaming',
        'complete',
        'error',
        'interrupted'
    ]),
    thread_id: z.string(),
    turn_id: z.string(),
    workflow_id: z.string().optional()
});

/**
 * Error body authored by the comfy-agent service and proxied verbatim through ingest. Errors raised by ingest itself (authentication, an unreachable agent service) use the standard ErrorResponse shape instead.
 */
export const zAgentError = z.object({
    error: z.string()
});

/**
 * Authoritative snapshot of a workflow's agent draft — the replay-on-reconnect baseline for the draft_patch stream.
 */
export const zAgentDraftSnapshot = z.object({
    content: z.record(z.unknown()),
    version: z.number().int()
});

/**
 * Acknowledgement that a turn cancellation was requested. Cancellation is asynchronous; the terminal message state (status=error with stop copy) arrives over the WebSocket.
 */
export const zAgentCancelAccepted = z.object({
    status: z.enum(['cancelling'])
});

/**
 * The user's answer to a pending ask_user prompt. `selected` holds the chosen option ids; a permission gate answers with one id (e.g. the Approve/Deny option). `other_text` carries a free-text answer when the ask set allow_other.
 */
export const zAgentAnswerRequest = z.object({
    other_text: z.string().optional(),
    selected: z.array(z.string())
});

/**
 * Acknowledgement that an ask answer was accepted. The parked turn resumes and its continuation streams over the WebSocket.
 */
export const zAgentAnswerAccepted = z.object({
    status: z.enum(['answered'])
});

/**
 * Response returned after successfully accepting a workspace invitation.
 */
export const zAcceptInviteResponse = z.object({
    workspace_id: z.string(),
    workspace_name: z.string()
});

/**
 * Represents a user-owned asset (image, video, or other generated output).
 */
export const zAssetWritable = z.object({
    created_at: z.string().datetime(),
    display_name: z.string().nullish(),
    file_path: z.string().nullish(),
    hash: z.string().regex(/^blake3:[a-f0-9]{64}$/).optional(),
    id: z.string().uuid(),
    is_immutable: z.boolean().optional(),
    job_id: z.string().uuid().nullish(),
    last_access_time: z.string().datetime().optional(),
    mime_type: z.string().optional(),
    name: z.string(),
    preview_id: z.string().uuid().nullish(),
    preview_url: z.string().url().optional(),
    short_url: z.string().nullish(),
    size: z.coerce.bigint().min(BigInt('-9223372036854775808'), { message: 'Invalid value: Expected int64 to be >= -9223372036854775808' }).max(BigInt('9223372036854775807'), { message: 'Invalid value: Expected int64 to be <= 9223372036854775807' }).optional(),
    tags: z.array(z.string()).optional(),
    updated_at: z.string().datetime(),
    user_metadata: z.record(z.unknown()).optional()
});

/**
 * Paginated list of assets belonging to the authenticated user.
 */
export const zListAssetsResponseWritable = z.object({
    assets: z.array(zAssetWritable),
    has_more: z.boolean(),
    next_cursor: z.string().optional(),
    total: z.number().int()
});

/**
 * Response returned when a new asset is successfully created.
 */
export const zAssetCreatedWritable = zAssetWritable.and(z.object({
    created_new: z.boolean()
}));

export const zGetJwksData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * JWKS response
 */
export const zGetJwksResponse = zJwksResponse;

export const zGetOAuthAuthorizationServerData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Authorization-server metadata
 */
export const zGetOAuthAuthorizationServerResponse = zOAuthAuthorizationServerMetadata;

export const zGetOAuthProtectedResourceData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Protected-resource metadata
 */
export const zGetOAuthProtectedResourceResponse = zOAuthProtectedResourceMetadata;

export const zGetOAuthProtectedResourceByPathData = z.object({
    body: z.never().optional(),
    path: z.object({
        resourcePath: z.string().regex(/^[a-zA-Z0-9._-]+$/)
    }),
    query: z.never().optional()
});

/**
 * Protected-resource metadata
 */
export const zGetOAuthProtectedResourceByPathResponse = zOAuthProtectedResourceMetadata;

export const zGetCustomNodeProxyData = z.object({
    body: z.never().optional(),
    path: z.object({
        path: z.string()
    }),
    query: z.never().optional()
});

export const zPostCustomNodeProxyData = z.object({
    body: z.never().optional(),
    path: z.object({
        path: z.string()
    }),
    query: z.never().optional()
});

export const zAgentGetDraftData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        workflow_id: z.string()
    })
});

/**
 * Current draft snapshot
 */
export const zAgentGetDraftResponse = zAgentDraftSnapshot;

export const zAgentListThreadsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        limit: z.number().int().gte(1).lte(100).optional().default(20),
        after: z.string().optional()
    }).optional()
});

/**
 * A page of the caller's agent threads.
 */
export const zAgentListThreadsResponse = zAgentThreadListResponse;

export const zAgentCreateThreadData = z.object({
    body: zAgentThreadCreateRequest.optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Thread created
 */
export const zAgentCreateThreadResponse = zAgentThreadCreated;

export const zAgentAnswerAskData = z.object({
    body: zAgentAnswerRequest,
    path: z.object({
        id: z.string(),
        ask_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Answer accepted; the turn resumes and streams over the WebSocket
 */
export const zAgentAnswerAskResponse = zAgentAnswerAccepted;

export const zAgentGetMessagesData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Messages in the thread, ordered by seq
 */
export const zAgentGetMessagesResponse = z.array(zAgentMessage);

export const zAgentPostMessageData = z.object({
    body: zAgentPostMessageRequest,
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Turn accepted; the agent runs asynchronously and streams output over the WebSocket
 */
export const zAgentPostMessageResponse = zAgentTurnAccepted;

export const zAgentCancelMessageData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string(),
        message_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Cancellation requested; the terminal state arrives over the WebSocket
 */
export const zAgentCancelMessageResponse = zAgentCancelAccepted;

export const zListAssetsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        include_tags: z.array(z.string()).optional(),
        exclude_tags: z.array(z.string()).optional(),
        name_contains: z.string().optional(),
        metadata_filter: z.string().optional(),
        limit: z.number().int().gte(1).lte(500).optional().default(20),
        offset: z.number().int().gte(0).optional().default(0),
        sort: z.enum([
            'name',
            'created_at',
            'updated_at',
            'size',
            'last_access_time'
        ]).optional(),
        order: z.enum(['asc', 'desc']).optional(),
        include_public: z.boolean().optional().default(true),
        hash: z.string().optional(),
        after: z.string().optional()
    }).optional()
});

/**
 * Success - Assets returned
 */
export const zListAssetsResponse2 = zListAssetsResponse;

export const zCreateAssetData = z.object({
    body: z.object({
        file: z.string(),
        hash: z.string().regex(/^(blake3|sha256):[a-f0-9]{64}$/).optional(),
        id: z.string().uuid().optional(),
        mime_type: z.string().optional(),
        name: z.string().optional(),
        preview_id: z.string().uuid().optional(),
        tags: z.string().optional(),
        user_metadata: z.string().optional()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Asset already existed for this user (deduplicated by content hash); the
 * existing asset is returned with created_new=false.
 *
 */
export const zCreateAssetResponse = zAssetCreated;

export const zDeleteAssetData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Asset record deleted successfully
 */
export const zDeleteAssetResponse = z.void();

export const zGetAssetByIdData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Asset details retrieved successfully
 */
export const zGetAssetByIdResponse = zAsset;

export const zUpdateAssetData = z.object({
    body: z.object({
        mime_type: z.string().optional(),
        name: z.string().optional(),
        preview_id: z.string().uuid().optional(),
        user_metadata: z.record(z.unknown()).optional()
    }),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Asset updated successfully
 */
export const zUpdateAssetResponse = zAssetUpdated;

export const zGetAssetContentData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.object({
        disposition: z.enum(['inline', 'attachment']).optional()
    }).optional()
});

/**
 * Asset content stream (local runtime streams the bytes directly)
 */
export const zGetAssetContentResponse = z.string();

export const zRemoveAssetTagsData = z.object({
    body: z.object({
        tags: z.array(z.string()).min(1)
    }),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Tags removed successfully
 */
export const zRemoveAssetTagsResponse = zTagsModificationResponse;

export const zAddAssetTagsData = z.object({
    body: z.object({
        tags: z.array(z.string()).min(1)
    }),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Tags added successfully
 */
export const zAddAssetTagsResponse = zTagsModificationResponse;

export const zCreateAssetDownloadData = z.object({
    body: z.object({
        preview_id: z.string().uuid().optional(),
        source_url: z.string().url(),
        tags: z.array(z.string()).optional(),
        user_metadata: z.record(z.unknown()).optional()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zCreateAssetDownloadResponse = z.union([
    zAssetCreated,
    zAssetDownloadResponse
]);

export const zCreateAssetExportData = z.object({
    body: z.object({
        asset_ids: z.array(z.string()).optional(),
        include_previews: z.boolean().optional().default(false),
        job_asset_name_filters: z.record(z.array(z.string()).min(1)).optional(),
        job_ids: z.array(z.string()).optional(),
        naming_strategy: z.enum([
            'group_by_job_id',
            'preserve',
            'asset_id',
            'group_by_job_time'
        ]).optional()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Accepted - Export task created and processing in background
 */
export const zCreateAssetExportResponse = zAssetDownloadResponse;

export const zDownloadExportData = z.object({
    body: z.never().optional(),
    path: z.object({
        exportName: z.string().regex(/^[a-zA-Z0-9_-]+\.zip$/)
    }),
    query: z.never().optional()
});

/**
 * Signed URL for downloading the export
 */
export const zDownloadExportResponse = zExportDownloadUrlResponse;

export const zCreateAssetFromHashData = z.object({
    body: z.object({
        hash: z.string().regex(/^blake3:[a-f0-9]{64}$/),
        mime_type: z.string().optional(),
        name: z.string().optional(),
        tags: z.array(z.string()).min(1),
        user_metadata: z.record(z.unknown()).optional()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Asset reference already existed for this user (deduplicated by content
 * hash); the existing asset is returned with created_new=false.
 *
 */
export const zCreateAssetFromHashResponse = zAssetCreated;

export const zPostAssetsFromWorkflowData = z.object({
    body: zWorkflowApiAssetsRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zPostAssetsFromWorkflowResponse = zWorkflowApiAssetsResponse;

export const zCheckAssetByHashData = z.object({
    body: z.never().optional(),
    path: z.object({
        hash: z.string().regex(/^blake3:[a-f0-9]{64}$/)
    }),
    query: z.never().optional()
});

export const zImportPublishedAssetsData = z.object({
    body: zImportPublishedAssetsRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Successfully imported assets
 */
export const zImportPublishedAssetsResponse2 = zImportPublishedAssetsResponse;

export const zPruneAssetsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Prune result
 */
export const zPruneAssetsResponse = z.object({
    marked: z.number().int().optional(),
    status: z.string().optional()
});

export const zGetRemoteAssetMetadataData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        url: z.string().url()
    })
});

/**
 * Metadata retrieved successfully
 */
export const zGetRemoteAssetMetadataResponse = zAssetMetadataResponse;

export const zSeedAssetsData = z.object({
    body: z.object({
        roots: z.array(z.string()).optional()
    }).optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Seed started
 */
export const zSeedAssetsResponse = z.object({
    status: z.string().optional()
});

export const zCancelAssetSeedData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Scan cancelled
 */
export const zCancelAssetSeedResponse = z.object({
    status: z.string().optional()
});

export const zGetAssetSeedStatusData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Scan progress details (files scanned, total, status, etc.)
 */
export const zGetAssetSeedStatusResponse = z.record(z.unknown());

export const zGetAssetTagHistogramData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        include_tags: z.array(z.string()).optional(),
        exclude_tags: z.array(z.string()).optional(),
        name_contains: z.string().optional(),
        metadata_filter: z.string().optional(),
        limit: z.number().int().gte(1).lte(1000).optional().default(100),
        include_public: z.boolean().optional().default(true)
    }).optional()
});

/**
 * Success - Tag histogram returned
 */
export const zGetAssetTagHistogramResponse = zAssetTagHistogramResponse;

export const zCreateDesktopLoginCodeData = z.object({
    body: zDesktopLoginCodeCreateRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Login code created
 */
export const zCreateDesktopLoginCodeResponse = zDesktopLoginCodeCreateResponse;

export const zExchangeDesktopLoginCodeData = z.object({
    body: zDesktopLoginCodeExchangeRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Pending (not yet redeemed) or complete with a custom token
 */
export const zExchangeDesktopLoginCodeResponse = zDesktopLoginCodeExchangeResponse;

export const zRedeemDesktopLoginCodeData = z.object({
    body: zDesktopLoginCodeRedeemRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Code redeemed (or already redeemed by the same user)
 */
export const zRedeemDesktopLoginCodeResponse = zDesktopLoginCodeRedeemResponse;

export const zDeleteSessionData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Session deleted successfully
 */
export const zDeleteSessionResponse2 = zDeleteSessionResponse;

export const zCreateSessionData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Session created successfully
 */
export const zCreateSessionResponse2 = zCreateSessionResponse;

export const zExchangeTokenData = z.object({
    body: zExchangeTokenRequest.optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Token exchanged successfully
 */
export const zExchangeTokenResponse2 = zExchangeTokenResponse;

export const zGetBillingBalanceData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Credit balance
 */
export const zGetBillingBalanceResponse = zBillingBalanceResponse;

export const zGetBillingEventsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        page: z.number().int().gte(1).optional().default(1),
        limit: z.number().int().gte(1).lte(100).optional().default(20),
        scope: z.enum([
            'self',
            'workspace',
            'user'
        ]).optional(),
        user_id: z.string().optional(),
        filter: z.string().optional(),
        start_date: z.string().datetime().optional(),
        end_date: z.string().datetime().optional()
    }).optional()
});

/**
 * Paginated billing events
 */
export const zGetBillingEventsResponse = zBillingEventsResponse;

export const zGetBillingOpStatusData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Billing operation status
 */
export const zGetBillingOpStatusResponse = zBillingOpStatusResponse;

export const zGetPaymentPortalData = z.object({
    body: zPaymentPortalRequest.optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetPaymentPortalResponse = zPaymentPortalResponse;

export const zGetBillingPlansData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Available plans with pricing
 */
export const zGetBillingPlansResponse = zBillingPlansResponse;

export const zPreviewSubscribeData = z.object({
    body: zPreviewSubscribeRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Subscription preview
 */
export const zPreviewSubscribeResponse2 = zPreviewSubscribeResponse;

export const zGetBillingStatusData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Billing status
 */
export const zGetBillingStatusResponse = zBillingStatusResponse;

export const zSubscribeData = z.object({
    body: zSubscribeRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Subscription created or payment method needed
 */
export const zSubscribeResponse2 = zSubscribeResponse;

export const zCancelSubscriptionData = z.object({
    body: zCancelSubscriptionRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Subscription cancellation scheduled
 */
export const zCancelSubscriptionResponse2 = zCancelSubscriptionResponse;

export const zResubscribeData = z.object({
    body: zResubscribeRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Subscription resumed successfully
 */
export const zResubscribeResponse2 = zResubscribeResponse;

export const zCreateTopupData = z.object({
    body: zCreateTopupRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Top-up initiated successfully
 */
export const zCreateTopupResponse2 = zCreateTopupResponse;

export const zGetBillingUsageTimeSeriesData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        group_by: z.enum([
            'model',
            'endpoint',
            'product'
        ]).optional(),
        granularity: z.enum([
            'hour',
            'day',
            'month'
        ]).optional(),
        starting_on: z.string().datetime().optional(),
        ending_before: z.string().datetime().optional(),
        months: z.number().int().gte(1).lte(24).optional().default(6)
    }).optional()
});

/**
 * Workspace usage
 */
export const zGetBillingUsageTimeSeriesResponse = zUsageTimeSeries;

export const zGetEmbeddingsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Embedding names
 */
export const zGetEmbeddingsResponse = z.array(z.string());

export const zGetModelFoldersData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success - List of model folders
 */
export const zGetModelFoldersResponse = z.array(zModelFolder);

export const zGetModelsInFolderData = z.object({
    body: z.never().optional(),
    path: z.object({
        folder: z.string()
    }),
    query: z.never().optional()
});

/**
 * Success - List of models in the folder
 */
export const zGetModelsInFolderResponse = z.array(zModelFile);

export const zGetModelPreviewData = z.object({
    body: z.never().optional(),
    path: z.object({
        folder: z.string(),
        path_index: z.number().int(),
        filename: z.string()
    }),
    query: z.never().optional()
});

export const zGetNodeInfoSchemaData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zGetNodeByIdData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

export const zGetExtensionsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * URL paths (relative to web root) of available extension JS files
 */
export const zGetExtensionsResponse = z.array(z.string());

export const zGetFeaturesData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetFeaturesResponse = z.object({
    free_tier_balance: z.object({
        allowance: z.number().int(),
        remaining: z.number().int(),
        used: z.number().int()
    }).optional(),
    max_upload_size: z.number().int().optional(),
    supports_preview_metadata: z.boolean().optional()
});

export const zSubmitFeedbackData = z.object({
    body: zFeedbackRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Feedback submitted successfully
 */
export const zSubmitFeedbackResponse = zFeedbackResponse;

export const zGetMaskLayersData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string()
    })
});

/**
 * Success - Related mask layers returned
 */
export const zGetMaskLayersResponse = z.object({
    mask: z.string().nullish(),
    paint: z.string().nullish(),
    painted: z.string().nullish(),
    painted_masked: z.string().nullish()
});

export const zFreeMemoryData = z.object({
    body: z.object({
        free_memory: z.boolean().optional(),
        unload_models: z.boolean().optional()
    }).optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zGetGlobalSubgraphsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success - Map of subgraph IDs to their metadata
 */
export const zGetGlobalSubgraphsResponse = z.record(zGlobalSubgraphInfo);

export const zGetGlobalSubgraphData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Success - Full subgraph data
 */
export const zGetGlobalSubgraphResponse = zGlobalSubgraphData;

export const zGetLegacyHistoryData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zManageHistoryData = z.object({
    body: zHistoryManageRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

export const zGetLegacyHistoryByIdData = z.object({
    body: z.never().optional(),
    path: z.object({
        prompt_id: z.string()
    }),
    query: z.never().optional()
});

export const zGetHistoryData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        max_items: z.number().int().optional(),
        offset: z.number().int().optional().default(0)
    }).optional()
});

/**
 * Success - Execution history retrieved
 */
export const zGetHistoryResponse = zHistoryResponse;

export const zGetHistoryForPromptData = z.object({
    body: z.never().optional(),
    path: z.object({
        prompt_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Success - History for prompt retrieved
 */
export const zGetHistoryForPromptResponse = zHistoryDetailResponse;

export const zCreateHubAssetUploadUrlData = z.object({
    body: zHubAssetUploadUrlRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Presigned upload URL and token
 */
export const zCreateHubAssetUploadUrlResponse = zHubAssetUploadUrlResponse;

export const zListHubLabelsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        type: z.enum([
            'tag',
            'model',
            'custom_node'
        ]).optional()
    }).optional()
});

/**
 * List of labels
 */
export const zListHubLabelsResponse = zHubLabelListResponse;

export const zCreateHubProfileData = z.object({
    body: zCreateHubProfileRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Hub profile created
 */
export const zCreateHubProfileResponse = zHubProfile;

export const zGetHubProfileByUsernameData = z.object({
    body: z.never().optional(),
    path: z.object({
        username: z.string()
    }),
    query: z.never().optional()
});

/**
 * Hub profile
 */
export const zGetHubProfileByUsernameResponse = zHubProfile;

export const zUpdateHubProfileData = z.object({
    body: zUpdateHubProfileRequest,
    path: z.object({
        username: z.string()
    }),
    query: z.never().optional()
});

/**
 * Hub profile updated
 */
export const zUpdateHubProfileResponse = zHubProfile;

export const zCheckHubUsernameData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        username: z.string()
    })
});

/**
 * Username availability result
 */
export const zCheckHubUsernameResponse = zHubUsernameCheckResponse;

export const zGetMyHubProfileData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Hub profile
 */
export const zGetMyHubProfileResponse = zHubProfile;

export const zListHubWorkflowsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        cursor: z.string().optional(),
        limit: z.number().int().gte(1).lte(100).optional().default(20),
        search: z.string().optional(),
        tag: z.string().optional(),
        username: z.string().optional(),
        detail: z.boolean().optional().default(false),
        status: z.array(zHubWorkflowStatus).optional()
    }).optional()
});

/**
 * Paginated list of hub workflows
 */
export const zListHubWorkflowsResponse = zHubWorkflowListResponse;

export const zPublishHubWorkflowData = z.object({
    body: zPublishHubWorkflowRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Workflow published to hub
 */
export const zPublishHubWorkflowResponse = zHubWorkflowDetail;

export const zDeleteHubWorkflowData = z.object({
    body: z.never().optional(),
    path: z.object({
        share_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Successfully unpublished
 */
export const zDeleteHubWorkflowResponse = z.void();

export const zGetHubWorkflowData = z.object({
    body: z.never().optional(),
    path: z.object({
        share_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Hub workflow detail
 */
export const zGetHubWorkflowResponse = zHubWorkflowDetail;

export const zListHubWorkflowIndexData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        status: z.array(zHubWorkflowStatus).optional()
    }).optional()
});

/**
 * List of hub workflow template entries
 */
export const zListHubWorkflowIndexResponse = z.array(zHubWorkflowTemplateEntry);

export const zGetI18nData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Nested map of locale to translation key-value pairs
 */
export const zGetI18nResponse = z.record(z.unknown());

export const zCreateInputUploadUrlData = z.object({
    body: zInputUploadUrlRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Upload grant minted
 */
export const zCreateInputUploadUrlResponse = zUploadGrantResponse;

export const zInterruptJobData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zAcceptWorkspaceInviteData = z.object({
    body: z.never().optional(),
    path: z.object({
        token: z.string()
    }),
    query: z.never().optional()
});

/**
 * Invite accepted, user is now a member
 */
export const zAcceptWorkspaceInviteResponse = zAcceptInviteResponse;

export const zGetLegacyJobByIdData = z.object({
    body: z.never().optional(),
    path: z.object({
        job_id: z.string()
    }),
    query: z.never().optional()
});

export const zGetLegacyJobOutputsData = z.object({
    body: z.never().optional(),
    path: z.object({
        job_id: z.string()
    }),
    query: z.never().optional()
});

export const zGetJobStatusData = z.object({
    body: z.never().optional(),
    path: z.object({
        job_id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Success - Job status returned
 */
export const zGetJobStatusResponse = zJobStatusResponse;

export const zListJobsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        status: z.string().optional(),
        workflow_id: z.string().optional(),
        output_type: z.enum([
            'image',
            'video',
            'audio',
            '3d'
        ]).optional(),
        sort_by: z.enum(['create_time', 'execution_time']).optional(),
        sort_order: z.enum(['asc', 'desc']).optional(),
        after: z.string().optional(),
        offset: z.number().int().gte(0).optional().default(0),
        limit: z.number().int().gte(1).lte(1000).optional().default(100)
    }).optional()
});

/**
 * Success - Jobs retrieved
 */
export const zListJobsResponse = zJobsListResponse;

export const zGetJobDetailData = z.object({
    body: z.never().optional(),
    path: z.object({
        job_id: z.string().uuid()
    }),
    query: z.object({
        short_link: z.enum(['ephemeral_tool_chain', 'default']).optional()
    }).optional()
});

/**
 * Success - Job details retrieved
 */
export const zGetJobDetailResponse = zJobDetailResponse;

export const zCancelJobData = z.object({
    body: z.never().optional(),
    path: z.object({
        job_id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Success - Cancel request accepted (or job was already terminal)
 */
export const zCancelJobResponse = zJobCancelResponse;

export const zCancelJobsData = z.object({
    body: zJobsCancelRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success - cancel requests dispatched (or jobs were already terminal)
 */
export const zCancelJobsResponse = zJobsCancelResponse;

export const zGetLegacyModelsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zGetLegacyModelsByFolderData = z.object({
    body: z.never().optional(),
    path: z.object({
        folder: z.string()
    }),
    query: z.never().optional()
});

export const zGetNodeReplacementsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success - Node replacement mappings
 */
export const zGetNodeReplacementsResponse = z.record(z.unknown());

export const zGetNodeInfoData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetNodeInfoResponse = z.record(zNodeInfo);

export const zGetLegacyObjectInfoByNodeClassData = z.object({
    body: z.never().optional(),
    path: z.object({
        node_class: z.string()
    }),
    query: z.never().optional()
});

export const zGetPromptInfoData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetPromptInfoResponse = zPromptInfo;

export const zExecutePromptData = z.object({
    body: zPromptRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success - Prompt accepted
 */
export const zExecutePromptResponse = zPromptResponse;

export const zGetLegacyPromptByIdData = z.object({
    body: z.never().optional(),
    path: z.object({
        prompt_id: z.string()
    }),
    query: z.never().optional()
});

export const zGetQueueInfoData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetQueueInfoResponse = zQueueInfo;

export const zManageQueueData = z.object({
    body: zQueueManageRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zManageQueueResponse = zQueueManageResponse;

export const zListSecretsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zListSecretsResponse = zSecretListResponse;

export const zCreateSecretData = z.object({
    body: zCreateSecretRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Secret created successfully
 */
export const zCreateSecretResponse = zSecretResponse;

export const zDeleteSecretData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Secret deleted successfully
 */
export const zDeleteSecretResponse = z.void();

export const zGetSecretData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetSecretResponse = zSecretResponse;

export const zUpdateSecretData = z.object({
    body: zUpdateSecretRequest,
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Secret updated successfully
 */
export const zUpdateSecretResponse = zSecretResponse;

export const zListSecretProvidersData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zListSecretProvidersResponse = zSecretProvidersResponse;

export const zGetAllSettingsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * User settings as key-value pairs
 */
export const zGetAllSettingsResponse = z.record(z.unknown());

export const zUpdateMultipleSettingsData = z.object({
    body: z.record(z.unknown()),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Updated user settings
 */
export const zUpdateMultipleSettingsResponse = z.record(z.unknown());

export const zGetSettingByIdData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Setting value response
 */
export const zGetSettingByIdResponse = z.object({
    value: z.unknown().optional()
});

export const zUpdateSettingByIdData = z.object({
    body: z.unknown(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Updated setting value response
 */
export const zUpdateSettingByIdResponse = z.object({
    value: z.unknown().optional()
});

export const zGetSystemStatsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetSystemStatsResponse = zSystemStatsResponse;

export const zListTagsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        prefix: z.string().optional(),
        limit: z.number().int().gte(1).lte(1000).optional().default(100),
        offset: z.number().int().gte(0).optional().default(0),
        order: z.enum(['count_desc', 'name_asc']).optional(),
        include_zero: z.boolean().optional().default(false),
        include_public: z.boolean().optional().default(true)
    }).optional()
});

/**
 * Tags retrieved successfully
 */
export const zListTagsResponse2 = zListTagsResponse;

export const zListTasksData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        task_name: z.string().optional(),
        idempotency_key: z.string().optional(),
        status: z.string().optional(),
        created_after: z.string().datetime().optional(),
        created_before: z.string().datetime().optional(),
        sort_order: z.enum(['asc', 'desc']).optional(),
        offset: z.number().int().gte(0).optional().default(0),
        limit: z.number().int().gte(1).lte(100).optional().default(20)
    }).optional()
});

/**
 * Success - Tasks retrieved
 */
export const zListTasksResponse = zTasksListResponse;

export const zGetTaskData = z.object({
    body: z.never().optional(),
    path: z.object({
        task_id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * Success - Task details retrieved
 */
export const zGetTaskResponse = zTaskResponse;

export const zUploadImageData = z.object({
    body: z.object({
        image: z.string(),
        overwrite: z.string().optional(),
        subfolder: z.string().optional(),
        type: z.string().optional()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Image uploaded successfully
 */
export const zUploadImageResponse = z.object({
    name: z.string().optional(),
    subfolder: z.string().optional(),
    type: z.string().optional()
});

export const zUploadMaskData = z.object({
    body: z.object({
        image: z.string(),
        original_ref: z.string()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Mask uploaded successfully
 */
export const zUploadMaskResponse = z.object({
    name: z.string().optional(),
    subfolder: z.string().optional(),
    type: z.string().optional()
});

export const zUploadGrantPutData = z.object({
    body: z.string(),
    path: z.object({
        upload_id: z.string()
    }),
    query: z.never().optional()
});

export const zUploadGrantPutResponse = z.union([
    zInputUploadResponse,
    zWorkflowResponse
]);

export const zGetUserData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetUserResponse = zUserResponse;

export const zGetUserdataData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        dir: z.string().optional(),
        recurse: z.boolean().optional().default(false),
        split: z.boolean().optional().default(false),
        full_info: z.boolean().optional().default(false)
    }).optional()
});

/**
 * A list of user data files.
 */
export const zGetUserdataResponse = zGetUserDataResponseFull;

export const zDeleteUserdataFileData = z.object({
    body: z.never().optional(),
    path: z.object({
        file: z.string()
    }),
    query: z.never().optional()
});

/**
 * File deleted successfully (No Content).
 */
export const zDeleteUserdataFileResponse = z.void();

export const zGetUserdataFileData = z.object({
    body: z.never().optional(),
    path: z.object({
        file: z.string()
    }),
    query: z.never().optional()
});

/**
 * Successfully retrieved the file.
 */
export const zGetUserdataFileResponse = z.string();

export const zPostUserdataFileData = z.object({
    body: z.string(),
    path: z.object({
        file: z.string()
    }),
    query: z.object({
        overwrite: z.enum(['true', 'false']).optional(),
        full_info: z.enum(['true', 'false']).optional()
    }).optional()
});

/**
 * File uploaded successfully.
 */
export const zPostUserdataFileResponse = zUserDataResponseFull;

export const zMoveUserdataFileData = z.object({
    body: z.never().optional(),
    path: z.object({
        file: z.string(),
        dest: z.string()
    }),
    query: z.object({
        overwrite: z.enum(['true', 'false']).optional()
    }).optional()
});

/**
 * File moved successfully.
 */
export const zMoveUserdataFileResponse = zUserDataResponseFull;

export const zGetUserdataFilePublishData = z.object({
    body: z.never().optional(),
    path: z.object({
        file: z.string()
    }),
    query: z.never().optional()
});

/**
 * Publish info (publish_time is null if never published)
 */
export const zGetUserdataFilePublishResponse = zWorkflowPublishInfo;

export const zPostUserdataFilePublishData = z.object({
    body: zPublishWorkflowAssetsRequest,
    path: z.object({
        file: z.string()
    }),
    query: z.never().optional()
});

/**
 * Workflow published
 */
export const zPostUserdataFilePublishResponse = zWorkflowPublishInfo;

export const zGetUsersInfoData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Userdata storage information
 */
export const zGetUsersInfoResponse = z.object({
    migrated: z.boolean(),
    storage: z.string()
});

export const zGetLegacyUserdataV2Data = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zGetVhsQueryVideoData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string()
    })
});

/**
 * Video metadata
 */
export const zGetVhsQueryVideoResponse = z.object({
    source: z.object({
        duration: z.number(),
        fps: z.number(),
        frames: z.number().int(),
        size: z.tuple([z.number().int(), z.number().int()])
    })
});

export const zGetVhsViewAudioData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string(),
        type: z.string().optional(),
        subfolder: z.string().optional()
    })
});

export const zGetVhsViewVideoData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string(),
        type: z.string().optional(),
        subfolder: z.string().optional()
    })
});

export const zViewFileData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string(),
        subfolder: z.string().optional(),
        type: z.string().optional(),
        fullpath: z.string().optional(),
        format: z.string().optional(),
        frame_rate: z.number().int().optional(),
        workflow: z.string().optional(),
        timestamp: z.number().int().optional(),
        channel: z.string().optional(),
        res: z.number().int().gte(64).lte(1024).optional()
    })
});

/**
 * Resized JPEG thumbnail (returned when res parameter is used)
 */
export const zViewFileResponse = z.string();

export const zGetLegacyViewMetadataData = z.object({
    body: z.never().optional(),
    path: z.object({
        folder_name: z.string()
    }),
    query: z.never().optional()
});

export const zGetApiViewVideoAliasData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string()
    })
});

export const zGetWorkflowTemplatesData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Empty object for workflow templates
 */
export const zGetWorkflowTemplatesResponse = z.record(z.unknown());

export const zListWorkflowsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        limit: z.number().int().lte(100).optional().default(20),
        offset: z.number().int().optional().default(0),
        name: z.string().optional(),
        default_view: z.enum(['workflow', 'app']).optional(),
        sort: z.enum([
            'create_time',
            'update_time',
            'name'
        ]).optional(),
        order: z.enum(['asc', 'desc']).optional()
    }).optional()
});

/**
 * Success
 */
export const zListWorkflowsResponse = zWorkflowListResponse;

export const zCreateWorkflowData = z.object({
    body: zCreateWorkflowRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Workflow created successfully
 */
export const zCreateWorkflowResponse = zWorkflowResponse;

export const zDeleteWorkflowData = z.object({
    body: z.never().optional(),
    path: z.object({
        workflow_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Workflow deleted successfully
 */
export const zDeleteWorkflowResponse = z.void();

export const zGetWorkflowData = z.object({
    body: z.never().optional(),
    path: z.object({
        workflow_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetWorkflowResponse = zWorkflowResponse;

export const zUpdateWorkflowData = z.object({
    body: zUpdateWorkflowRequest,
    path: z.object({
        workflow_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Success
 */
export const zUpdateWorkflowResponse = zWorkflowResponse;

export const zGetWorkflowContentData = z.object({
    body: z.never().optional(),
    path: z.object({
        workflow_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Success
 */
export const zGetWorkflowContentResponse = zWorkflowVersionContentResponse;

export const zForkWorkflowData = z.object({
    body: zForkWorkflowRequest,
    path: z.object({
        workflow_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Workflow forked successfully
 */
export const zForkWorkflowResponse = zWorkflowResponse;

export const zCreateWorkflowVersionData = z.object({
    body: zCreateWorkflowVersionRequest,
    path: z.object({
        workflow_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Version created successfully
 */
export const zCreateWorkflowVersionResponse = zWorkflowVersionResponse;

export const zGetPublishedWorkflowData = z.object({
    body: z.never().optional(),
    path: z.object({
        share_id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Published workflow details with asset statuses
 */
export const zGetPublishedWorkflowResponse = zPublishedWorkflowDetail;

export const zCreateWorkflowUploadUrlData = z.object({
    body: zWorkflowUploadUrlRequest.optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Upload grant minted
 */
export const zCreateWorkflowUploadUrlResponse = zUploadGrantResponse;

export const zListWorkspaceApiKeysData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * List of API keys
 */
export const zListWorkspaceApiKeysResponse2 = zListWorkspaceApiKeysResponse;

export const zCreateWorkspaceApiKeyData = z.object({
    body: zCreateWorkspaceApiKeyRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * API key created (plaintext returned once)
 */
export const zCreateWorkspaceApiKeyResponse2 = zCreateWorkspaceApiKeyResponse;

export const zRevokeWorkspaceApiKeyData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string().uuid()
    }),
    query: z.never().optional()
});

/**
 * API key revoked
 */
export const zRevokeWorkspaceApiKeyResponse = z.void();

export const zListWorkspaceInvitesData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * List of pending invites
 */
export const zListWorkspaceInvitesResponse = zListInvitesResponse;

export const zCreateWorkspaceInviteData = z.object({
    body: zCreateInviteRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Invite created
 */
export const zCreateWorkspaceInviteResponse = zPendingInvite;

export const zRevokeWorkspaceInviteData = z.object({
    body: z.never().optional(),
    path: z.object({
        inviteId: z.string()
    }),
    query: z.never().optional()
});

/**
 * Invite revoked
 */
export const zRevokeWorkspaceInviteResponse = z.void();

export const zResendWorkspaceInviteData = z.object({
    body: z.never().optional(),
    path: z.object({
        inviteId: z.string()
    }),
    query: z.never().optional()
});

/**
 * Invite refreshed and queued for delivery
 */
export const zResendWorkspaceInviteResponse = zPendingInvite;

export const zLeaveWorkspaceData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Successfully left workspace
 */
export const zLeaveWorkspaceResponse = z.void();

export const zListWorkspaceMembersData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        offset: z.number().int().gte(0).optional().default(0),
        limit: z.number().int().gte(1).lte(100).optional().default(20)
    }).optional()
});

/**
 * List of members
 */
export const zListWorkspaceMembersResponse = zListMembersResponse;

export const zBulkRevokeWorkspaceMemberApiKeysData = z.object({
    body: z.never().optional(),
    path: z.object({
        user_id: z.string().min(1)
    }),
    query: z.never().optional()
});

/**
 * Keys revoked successfully
 */
export const zBulkRevokeWorkspaceMemberApiKeysResponse = zBulkRevokeApiKeysResponse;

export const zRemoveWorkspaceMemberData = z.object({
    body: z.never().optional(),
    path: z.object({
        userId: z.string()
    }),
    query: z.never().optional()
});

/**
 * Member removed
 */
export const zRemoveWorkspaceMemberResponse = z.void();

export const zUpdateWorkspaceMemberRoleData = z.object({
    body: zUpdateMemberRoleRequest,
    path: z.object({
        userId: z.string()
    }),
    query: z.never().optional()
});

/**
 * Member role updated
 */
export const zUpdateWorkspaceMemberRoleResponse = zMember;

export const zListWorkspacesData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * List of workspaces
 */
export const zListWorkspacesResponse2 = zListWorkspacesResponse;

export const zCreateWorkspaceData = z.object({
    body: zCreateWorkspaceRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Workspace created
 */
export const zCreateWorkspaceResponse = zWorkspace;

export const zDeleteWorkspaceData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Workspace deleted
 */
export const zDeleteWorkspaceResponse = z.void();

export const zGetWorkspaceData = z.object({
    body: z.never().optional(),
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Workspace details
 */
export const zGetWorkspaceResponse = zWorkspace;

export const zUpdateWorkspaceData = z.object({
    body: zUpdateWorkspaceRequest,
    path: z.object({
        id: z.string()
    }),
    query: z.never().optional()
});

/**
 * Workspace updated
 */
export const zUpdateWorkspaceResponse = zWorkspace;

export const zGetStaticExtensionsData = z.object({
    body: z.never().optional(),
    path: z.object({
        path: z.string()
    }),
    query: z.never().optional()
});

export const zGetHealthData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Service is healthy
 */
export const zGetHealthResponse = z.string();

export const zGetInternalFolderPathsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Map of folder type name to list of path entries
 */
export const zGetInternalFolderPathsResponse = z.record(z.array(z.array(z.string())));

export const zGetInternalLogsData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Log text
 */
export const zGetInternalLogsResponse = z.string();

export const zGetInternalLogsRawData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Structured log data
 */
export const zGetInternalLogsRawResponse = z.object({
    entries: z.array(z.object({
        m: z.string().optional(),
        t: z.number().optional()
    })).optional(),
    size: z.object({
        cols: z.number().int().optional(),
        rows: z.number().int().optional()
    }).optional()
});

export const zSubscribeToLogsData = z.object({
    body: z.object({
        clientId: z.string(),
        enabled: z.boolean()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

export const zGetOAuthAuthorizeData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        response_type: z.string().optional(),
        client_id: z.string().optional(),
        redirect_uri: z.string().optional(),
        scope: z.string().optional(),
        state: z.string().optional(),
        code_challenge: z.string().optional(),
        code_challenge_method: z.string().optional(),
        resource: z.string().optional(),
        oauth_request_id: z.string().optional()
    }).optional()
});

/**
 * Consent challenge payload (cookie present, email verified). Frontend renders the consent UI from this payload and POSTs back to /oauth/authorize.
 *
 */
export const zGetOAuthAuthorizeResponse = zOAuthConsentChallenge;

export const zPostOAuthAuthorizeData = z.object({
    body: z.object({
        csrf_token: z.string(),
        decision: z.enum(['allow', 'deny']),
        oauth_request_id: z.string().uuid(),
        workspace_id: z.string()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Redirect URL for the frontend to navigate to (allow → with code+state; deny → with error+state)
 */
export const zPostOAuthAuthorizeResponse = zOAuthAuthorizeRedirectResponse;

export const zPostOAuthRegisterData = z.object({
    body: zOAuthRegisterRequest,
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * Registered. Body echoes the metadata RFC 7591 §3.2.1 requires.
 */
export const zPostOAuthRegisterResponse = zOAuthRegisterResponse;

export const zPostOAuthTokenData = z.object({
    body: z.object({
        client_id: z.string(),
        client_secret: z.string().optional(),
        code: z.string().optional(),
        code_verifier: z.string().optional(),
        grant_type: z.enum(['authorization_code', 'refresh_token']),
        redirect_uri: z.string().optional(),
        refresh_token: z.string().optional(),
        scope: z.string().optional()
    }),
    path: z.never().optional(),
    query: z.never().optional()
});

/**
 * New token pair
 */
export const zPostOAuthTokenResponse = zOAuthTokenResponse;

export const zGetTemplateProxyData = z.object({
    body: z.never().optional(),
    path: z.object({
        path: z.string()
    }),
    query: z.never().optional()
});

export const zGetViewCompatAliasData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        filename: z.string()
    })
});

export const zGetWebsocketData = z.object({
    body: z.never().optional(),
    path: z.never().optional(),
    query: z.object({
        clientId: z.string().optional()
    }).optional()
});
